// src/services/authService.js - Enhanced with security fixes and memory management
import axios from "axios";

// Secure storage utility
class SecureStorage {
  static setItem(key, value) {
    try {
      const data = typeof value === "string" ? value : JSON.stringify(value);
      // Use sessionStorage for sensitive data with basic encoding
      sessionStorage.setItem(key, btoa(data));
    } catch (error) {
      console.warn("Failed to store auth data securely:", error);
      // Fallback to localStorage with warning
      localStorage.setItem(
        key,
        typeof value === "string" ? value : JSON.stringify(value)
      );
    }
  }

  static getItem(key) {
    try {
      let data = sessionStorage.getItem(key);
      if (data) {
        return atob(data);
      }
      // Fallback to localStorage
      data = sessionStorage.getItem(key);
      if (data) {
        console.warn(
          `Found ${key} in localStorage, migrating to sessionStorage`
        );
        SecureStorage.setItem(key, data);
        localStorage.removeItem(key);
        return data;
      }
      return null;
    } catch (error) {
      console.warn("Failed to retrieve auth data:", error);
      return sessionStorage.getItem(key);
    }
  }

  static removeItem(key) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key); // Clean both storages
  }
}

// Rate limiter for authentication attempts
class AuthRateLimiter {
  constructor(maxAttempts = 5, windowMs = 15 * 60 * 1000) {
    // 5 attempts per 15 minutes
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();

    // Clean up old attempts periodically
    setInterval(() => {
      this.cleanup();
    }, this.windowMs);
  }

  isAllowed(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];

    // Remove old attempts
    const recentAttempts = userAttempts.filter(
      (timestamp) => now - timestamp < this.windowMs
    );
    this.attempts.set(identifier, recentAttempts);

    return recentAttempts.length < this.maxAttempts;
  }

  recordAttempt(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];
    userAttempts.push(now);
    this.attempts.set(identifier, userAttempts);
  }

  getRemainingTime(identifier) {
    const userAttempts = this.attempts.get(identifier) || [];
    if (userAttempts.length === 0) return 0;

    const oldestAttempt = Math.min(...userAttempts);
    const timeLeft = this.windowMs - (Date.now() - oldestAttempt);
    return Math.max(0, timeLeft);
  }

  cleanup() {
    const now = Date.now();
    for (const [identifier, attempts] of this.attempts.entries()) {
      const recentAttempts = attempts.filter(
        (timestamp) => now - timestamp < this.windowMs
      );
      if (recentAttempts.length === 0) {
        this.attempts.delete(identifier);
      } else {
        this.attempts.set(identifier, recentAttempts);
      }
    }
  }

  getStats() {
    return {
      activeUsers: this.attempts.size,
      maxAttempts: this.maxAttempts,
      windowMs: this.windowMs,
    };
  }
}

class AuthService {
  constructor() {
    this.apiUrl =
      process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === "production"
        ? "/api/v1"
        : "http://localhost:8000/api/v1");
    this.tokenKey = "token";
    this.userKey = "user";
    this.securityLevelKey = "security_level";
    this.deviceFingerprintKey = "device_fingerprint";

    // Enhanced cache system with memory management
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    this.MAX_CACHE_SIZE = 50;
    this.MAX_MEMORY_MB = 10;

    // Cache strategies for different endpoints
    this.CACHE_STRATEGIES = {
      security_status: { ttl: 30000, priority: "high" },
      current_user: { ttl: 60000, priority: "high" },
      security_logs: { ttl: 120000, priority: "medium" },
      default: { ttl: 300000, priority: "low" },
    };

    // Request deduplication with cleanup
    this.pendingRequests = new Map();

    // Rate limiting for auth attempts
    this.rateLimiter = new AuthRateLimiter();

    // Session monitoring
    this.sessionCheckInterval = null;
    this.sessionWarningTimeout = null;
    this.activityTimeout = null;
    this.lastActivityTime = Date.now();
    this.activityCheckInterval = null;
    this.DEFAULT_IDLE_TIMEOUT = 60 * 60 * 1000; // 60 minutes for most users
    this.MP_IDLE_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours for medical professionals
    this.IDLE_TIMEOUT = this.DEFAULT_IDLE_TIMEOUT; // Will be updated on login based on role
    this.TOKEN_REFRESH_THRESHOLD = 15 * 60 * 1000; // Refresh token if less than 10 min remaining
    this.isUserActive = false;

    // Retry configuration
    this.maxRetries = 10;
    this.retryDelay = 1000;

    this.setupInterceptors();
    this.startCacheCleanup();
    this.startMemoryMonitoring();
    this.startSessionMonitoring();
  }

  setupInterceptors() {
    // Enhanced request interceptor
    axios.interceptors.request.use(
      (config) => {
        const token = SecureStorage.getItem(this.tokenKey);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add security headers
        config.timeout = config.timeout || 30000;
        config.headers["X-Request-ID"] = this.generateRequestId();
        config.headers["X-Client-Version"] =
          process.env.REACT_APP_VERSION || "1.0.0";

        // Add device fingerprint for additional security
        const fingerprint = this.getDeviceFingerprint();
        config.headers["X-Device-ID"] = fingerprint.substring(0, 16);

        // Add CSRF protection
        const csrfToken = document.querySelector(
          'meta[name="csrf-token"]'
        )?.content;
        if (csrfToken) {
          config.headers["X-CSRF-Token"] = csrfToken;
        }

        return config;
      },
      (error) => {
        console.error("🔐 Request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    // Enhanced response interceptor
    axios.interceptors.response.use(
      (response) => {
        this.logSecurityEvent("API_REQUEST_SUCCESS", {
          url: response.config.url,
          method: response.config.method,
          status: response.status,
        });
        return response;
      },
      async (error) => {
        if (!error.response) {
          console.error("🌐 Network error:", error.message);
          this.logSecurityEvent("NETWORK_ERROR", {
            error: error.message,
            url: error.config?.url,
          });
          throw new Error(
            "Network connection failed. Please check your connection and try again."
          );
        }

        const { status, config } = error.response;
        const retryCount = config.retryCount || 0;

        this.logSecurityEvent("API_REQUEST_ERROR", {
          url: config.url,
          method: config.method,
          status: status,
          error: error.response.data?.detail || error.message,
          retryCount,
        });

        // Enhanced retry logic for authentication requests
        if (this.shouldRetryAuthRequest(error, config)) {
          return this.retryAuthRequest(config);
        }

        // Handle 401 with improved token refresh
        if (status === 401) {
          return this.handle401Error(config);
        }

        // Handle rate limiting
        if (status === 429) {
          const retryAfter =
            parseInt(error.response.headers["retry-after"]) || 60;
          this.logSecurityEvent("RATE_LIMITED", {
            retryAfter,
            url: config.url,
          });

          throw new Error(
            `Too many requests. Please wait ${retryAfter} seconds before trying again.`
          );
        }

        return Promise.reject(error);
      }
    );
  }

  // Enhanced cache management
  getCachedResponse(key) {
    const cached = this.cache.get(key);
    const timestamp = this.cacheTimestamps.get(key);
    const strategy = this.getCacheStrategy(key);

    if (cached && timestamp && Date.now() - timestamp < strategy.ttl) {
      return cached;
    }

    // Clear expired cache
    this.cache.delete(key);
    this.cacheTimestamps.delete(key);
    return null;
  }

  setCachedResponse(key, response) {
    // Check memory usage
    if (this.getMemoryUsage() > this.MAX_MEMORY_MB * 1024 * 1024) {
      this.evictOldCache();
    }

    // Prevent cache from growing too large
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      this.evictOldCache();
    }

    this.cache.set(key, response);
    this.cacheTimestamps.set(key, Date.now());
  }

  getCacheStrategy(key) {
    return this.CACHE_STRATEGIES[key] || this.CACHE_STRATEGIES.default;
  }

  getMemoryUsage() {
    let totalSize = 0;
    for (const [key, value] of this.cache.entries()) {
      totalSize += new Blob([JSON.stringify(value)]).size;
    }
    return totalSize;
  }

  evictOldCache() {
    const entries = Array.from(this.cacheTimestamps.entries()).sort(
      ([, a], [, b]) => a - b
    );

    // Remove oldest 25%
    const toRemove = entries.slice(0, Math.ceil(entries.length * 0.25));
    toRemove.forEach(([key]) => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
    });
  }

  startCacheCleanup() {
    setInterval(() => {
      this.cleanupExpiredCache();
    }, 60000); // Clean every minute
  }

  cleanupExpiredCache() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      const strategy = this.getCacheStrategy(key);
      if (now - timestamp > strategy.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
    });

    if (keysToDelete.length > 0 && process.env.NODE_ENV === "development") {
      console.log(
        `🧹 Auth cache cleanup: removed ${keysToDelete.length} expired entries`
      );
    }
  }

  startMemoryMonitoring() {
    setInterval(() => {
      const memoryUsage = this.getMemoryUsage();
      const memoryMB = memoryUsage / (1024 * 1024);

      if (memoryMB > this.MAX_MEMORY_MB * 0.8) {
        console.warn(
          `⚠️ Auth service high memory usage: ${memoryMB.toFixed(2)}MB`
        );
        this.evictOldCache();
      }
    }, 120000); // Check every 2 minutes
  }

  // Enhanced request deduplication
  async deduplicateRequest(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      console.log(`🔄 Deduplicating auth request: ${key}`);
      return this.pendingRequests.get(key);
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  // Enhanced login with rate limiting
  async login(username, password, recaptchaToken) {
    const identifier = this.hashIdentifier(username);

    // Check rate limiting
    if (!this.rateLimiter.isAllowed(identifier)) {
      const remainingTime = Math.ceil(
        this.rateLimiter.getRemainingTime(identifier) / 1000
      );
      this.logSecurityEvent("LOGIN_RATE_LIMITED", {
        username: this.maskUsername(username),
        remainingTime,
      });

      return {
        success: false,
        message: `Too many login attempts. Please wait ${remainingTime} seconds before trying again.`,
        rateLimited: true,
        remainingTime,
      };
    }

    try {
      this.logSecurityEvent("LOGIN_ATTEMPT_START", {
        username: this.maskUsername(username),
        device_fingerprint:
          this.getDeviceFingerprint().substring(0, 10) + "...",
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/login`,
        { username, password, recaptcha_token: recaptchaToken || null },
        { timeout: 15000 }
      );

      const data = response.data;

      if (data.success) {
        this.setAuthData({
          access_token: data.access_token,
          user: data.user,
          security_level: data.security_level,
        });

        this.logSecurityEvent("LOGIN_SUCCESS", {
          username: this.maskUsername(username),
          user_id: data.user.id,
          security_level: data.security_level,
        });

        // Clear rate limiting on successful login
        this.rateLimiter.attempts.delete(identifier);

        return {
          success: true,
          token: data.access_token,
          user: data.user,
          security_level: data.security_level,
          message: data.message,
        };
      } else {
        // Record failed attempt
        this.rateLimiter.recordAttempt(identifier);

        this.logSecurityEvent("LOGIN_REQUIRES_ADDITIONAL_AUTH", {
          username: this.maskUsername(username),
          requires_2fa: data.requires_2fa,
          setup_required: data.setup_required,
        });

        return {
          success: false,
          message: data.message,
          requires_2fa: data.requires_2fa,
          setup_required: data.setup_required,
          setup_methods: data.setup_methods,
          setup_token: data.setup_token, // Limited-scope token for 2FA setup
          available_2fa_methods: data.available_2fa_methods,
          user: data.user,
          sms_sent: data.sms_sent, // Include SMS status
          expires_in: data.expires_in,
        };
      }
    } catch (error) {
      // Record failed attempt
      this.rateLimiter.recordAttempt(identifier);

      console.error("🔐 Login error:", error);
      this.logSecurityEvent("LOGIN_ERROR", {
        username: this.maskUsername(username),
        error: error.message,
        status: error.response?.status,
      });

      // FIXED: Extract proper error message from response
      return this.handleAuthError(error, "Login failed");
    }
  }

  // Enhanced 2FA login with retry logic
  async loginWith2FA(username, password, smsCode = null, totpCode = null) {
    const identifier = this.hashIdentifier(username);

    if (!this.rateLimiter.isAllowed(identifier)) {
      const remainingTime = Math.ceil(
        this.rateLimiter.getRemainingTime(identifier) / 1000
      );
      return {
        success: false,
        message: `Too many attempts. Please wait ${remainingTime} seconds.`,
        rateLimited: true,
        remainingTime,
      };
    }

    try {
      this.logSecurityEvent("2FA_LOGIN_ATTEMPT", {
        username: this.maskUsername(username),
        has_sms_code: !!smsCode,
        has_totp_code: !!totpCode,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/login-with-2fa`,
        {
          username,
          password,
          sms_code: smsCode,
          totp_code: totpCode,
        },
        { timeout: 15000 }
      );

      const data = response.data;

      if (data.success) {
        this.setAuthData({
          access_token: data.access_token,
          user: data.user,
          security_level: data.security_level,
        });

        this.logSecurityEvent("2FA_LOGIN_SUCCESS", {
          username: this.maskUsername(username),
          user_id: data.user.id,
          security_level: data.security_level,
          method: smsCode ? "sms" : "totp",
        });

        // Clear rate limiting on successful login
        this.rateLimiter.attempts.delete(identifier);

        return {
          success: true,
          token: data.access_token,
          user: data.user,
          security_level: data.security_level,
          message: data.message,
        };
      } else {
        this.rateLimiter.recordAttempt(identifier);

        this.logSecurityEvent("2FA_LOGIN_FAILED", {
          username: this.maskUsername(username),
          message: data.message,
          method: smsCode ? "sms" : "totp",
        });

        return {
          success: false,
          message: data.message,
          requires_2fa: data.requires_2fa,
          available_2fa_methods: data.available_2fa_methods,
        };
      }
    } catch (error) {
      this.rateLimiter.recordAttempt(identifier);

      console.error("🔐 2FA login error:", error);
      this.logSecurityEvent("2FA_LOGIN_ERROR", {
        username: this.maskUsername(username),
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "2FA login failed");
    }
  }

  // Enhanced SMS code request with rate limiting
  async requestSMSCode(username, password) {
    const identifier = this.hashIdentifier(username);

    if (!this.rateLimiter.isAllowed(identifier)) {
      const remainingTime = Math.ceil(
        this.rateLimiter.getRemainingTime(identifier) / 1000
      );
      return {
        success: false,
        message: `Too many SMS requests. Please wait ${remainingTime} seconds.`,
        rateLimited: true,
      };
    }

    try {
      this.logSecurityEvent("SMS_CODE_REQUEST", {
        username: this.maskUsername(username),
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/request-sms-code`,
        { username, password },
        { timeout: 15000 }
      );

      if (response.data.success) {
        this.logSecurityEvent("SMS_CODE_SENT", {
          username: this.maskUsername(username),
          expires_in: response.data.expires_in,
        });
      } else {
        this.rateLimiter.recordAttempt(identifier);
      }

      return {
        success: response.data.success,
        message: response.data.message,
        expires_in: response.data.expires_in,
      };
    } catch (error) {
      this.rateLimiter.recordAttempt(identifier);

      console.error("🔐 Request SMS code error:", error);
      this.logSecurityEvent("SMS_CODE_REQUEST_ERROR", {
        username: this.maskUsername(username),
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to send SMS code");
    }
  }

  // Enhanced 2FA setup
  async setup2FA(phoneNumber, countryCode = "+1", verificationCode = null) {
    try {
      this.logSecurityEvent("2FA_SETUP_START", {
        phone_masked: this.maskPhoneNumber(phoneNumber),
        country_code: countryCode,
        immediate_verification: !!verificationCode,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/setup-2fa`,
        {
          phone_number: phoneNumber,
          country_code: countryCode,
          verification_code: verificationCode,
        },
        { timeout: 15000 }
      );

      const result = {
        success: response.data.success,
        message: response.data.message,
        phone_number: response.data.phone_number,
        requires_verification: response.data.requires_verification,
        backup_codes: response.data.backup_codes || [],
        qr_code: response.data.qr_code,
      };

      this.logSecurityEvent("2FA_SETUP_RESULT", {
        success: result.success,
        requires_verification: result.requires_verification,
        has_backup_codes: result.backup_codes.length > 0,
      });

      // Clear security status cache
      this.cache.delete("security_status");
      this.cacheTimestamps.delete("security_status");

      return result;
    } catch (error) {
      console.error("🔐 2FA setup error:", error);
      this.logSecurityEvent("2FA_SETUP_ERROR", {
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to setup 2FA");
    }
  }

  // Enhanced verify 2FA setup
  async verify2FASetup(verificationCode) {
    try {
      this.logSecurityEvent("2FA_SETUP_VERIFICATION", {
        code_provided: !!verificationCode,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/verify-2fa-setup`,
        { verification_code: verificationCode },
        { timeout: 15000 }
      );

      const result = {
        success: response.data.success,
        message: response.data.message,
        backup_codes: response.data.backup_codes || [],
      };

      this.logSecurityEvent("2FA_SETUP_VERIFICATION_RESULT", {
        success: result.success,
        has_backup_codes: result.backup_codes.length > 0,
      });

      // Clear security status cache
      this.cache.delete("security_status");
      this.cacheTimestamps.delete("security_status");

      return result;
    } catch (error) {
      console.error("🔐 2FA verification error:", error);
      this.logSecurityEvent("2FA_SETUP_VERIFICATION_ERROR", {
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to verify 2FA setup");
    }
  }

  // 2FA setup using a limited-scope setup token (for mandatory 2FA enrollment)
  async setup2FAWithToken(
    setupToken,
    phoneNumber,
    countryCode = "+1",
    verificationCode = null
  ) {
    try {
      this.logSecurityEvent("2FA_SETUP_WITH_TOKEN_START", {
        phone_masked: this.maskPhoneNumber(phoneNumber),
        country_code: countryCode,
        immediate_verification: !!verificationCode,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/setup-2fa-with-token`,
        {
          phone_number: phoneNumber,
          country_code: countryCode,
          verification_code: verificationCode,
        },
        {
          timeout: 15000,
          headers: { Authorization: `Bearer ${setupToken}` },
        }
      );

      const result = {
        success: response.data.success,
        message: response.data.message,
        phone_number: response.data.phone_number,
        requires_verification: response.data.requires_verification,
        backup_codes: response.data.backup_codes || [],
        qr_code: response.data.qr_code,
      };

      this.logSecurityEvent("2FA_SETUP_WITH_TOKEN_RESULT", {
        success: result.success,
        requires_verification: result.requires_verification,
        has_backup_codes: result.backup_codes.length > 0,
      });

      return result;
    } catch (error) {
      console.error("2FA setup (with token) error:", error);
      this.logSecurityEvent("2FA_SETUP_WITH_TOKEN_ERROR", {
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to setup 2FA");
    }
  }

  // Verify 2FA setup using a limited-scope setup token
  async verify2FASetupWithToken(setupToken, verificationCode) {
    try {
      this.logSecurityEvent("2FA_VERIFY_WITH_TOKEN_START", {
        code_provided: !!verificationCode,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/verify-2fa-setup-with-token`,
        { verification_code: verificationCode },
        {
          timeout: 15000,
          headers: { Authorization: `Bearer ${setupToken}` },
        }
      );

      const result = {
        success: response.data.success,
        message: response.data.message,
        backup_codes: response.data.backup_codes || [],
      };

      this.logSecurityEvent("2FA_VERIFY_WITH_TOKEN_RESULT", {
        success: result.success,
        has_backup_codes: result.backup_codes.length > 0,
      });

      return result;
    } catch (error) {
      console.error("2FA verification (with token) error:", error);
      this.logSecurityEvent("2FA_VERIFY_WITH_TOKEN_ERROR", {
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to verify 2FA setup");
    }
  }

  // Enhanced VPN setup
  async setupVPNAccess(allowedIpRanges) {
    try {
      this.logSecurityEvent("VPN_SETUP_ATTEMPT", {
        ip_ranges_count: allowedIpRanges.length,
        user_id: this.getCurrentUserFromStorage()?.id,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/setup-vpn-access`,
        { allowed_ip_ranges: allowedIpRanges },
        { timeout: 15000 }
      );

      this.logSecurityEvent("VPN_SETUP_SUCCESS", {
        user_id: this.getCurrentUserFromStorage()?.id,
        expires_at: response.data.expires_at,
      });

      // Clear security status cache
      this.cache.delete("security_status");
      this.cacheTimestamps.delete("security_status");

      return {
        success: response.data.success,
        api_key: response.data.api_key,
        expires_at: response.data.expires_at,
        allowed_ip_ranges: response.data.allowed_ip_ranges,
        message: response.data.message,
      };
    } catch (error) {
      console.error("🔐 VPN setup error:", error);
      this.logSecurityEvent("VPN_SETUP_ERROR", {
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to setup VPN access");
    }
  }

  // Disable 2FA
  async disable2FA(password) {
    try {
      this.logSecurityEvent("2FA_DISABLE_ATTEMPT", {
        user_id: this.getCurrentUserFromStorage()?.id,
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/disable-2fa`,
        { password },
        { timeout: 15000 }
      );

      this.logSecurityEvent("2FA_DISABLED", {
        user_id: this.getCurrentUserFromStorage()?.id,
      });

      // Clear security status cache
      this.cache.delete("security_status");
      this.cacheTimestamps.delete("security_status");

      return {
        success: response.data.success,
        message: response.data.message,
      };
    } catch (error) {
      console.error("🔐 Disable 2FA error:", error);
      this.logSecurityEvent("2FA_DISABLE_ERROR", {
        error: error.message,
        status: error.response?.status,
      });
      return this.handleAuthError(error, "Failed to disable 2FA");
    }
  }

  // Enhanced security status with caching and deduplication
  async getSecurityStatus() {
    const cacheKey = "security_status";

    // Check cache first
    const cached = this.getCachedResponse(cacheKey);
    if (cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const response = await axios.get(
          `${this.apiUrl}/auth/security-status`,
          { timeout: 10000 }
        );

        const result = {
          success: true,
          ...response.data,
        };

        // Cache the result
        this.setCachedResponse(cacheKey, result);
        return result;
      } catch (error) {
        console.error("🔐 Get security status error:", error);

        // Return default status if server is unreachable
        const defaultResult = {
          success: false,
          sms_2fa_enabled: false,
          totp_2fa_enabled: false,
          vpn_required: false,
          api_key_configured: false,
          device_authorized: false,
          last_login: null,
          failed_attempts: 0,
          account_locked: false,
          security_score: 0,
          recommendations: [
            "Unable to load security status - server unavailable",
          ],
        };

        // Cache the default result for a shorter time
        this.setCachedResponse(cacheKey, defaultResult);
        return defaultResult;
      }
    });
  }

  // Enhanced security logs with caching
  async getSecurityLogs(limit = 50) {
    const cacheKey = `security_logs_${limit}`;

    // Check cache first
    const cached = this.getCachedResponse(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await axios.get(`${this.apiUrl}/auth/security-logs`, {
        params: { limit },
        timeout: 10000,
      });

      const result = {
        success: true,
        logs: response.data.logs || [],
      };

      // Cache the result
      this.setCachedResponse(cacheKey, result);
      return result;
    } catch (error) {
      console.error("🔐 Get security logs error:", error);
      const result = {
        success: false,
        logs: [],
        message: "Unable to load security logs - server unavailable",
      };

      // Cache the error result for a shorter time
      this.setCachedResponse(cacheKey, result);
      return result;
    }
  }

  // Enhanced current user with caching and fallback
  async getCurrentUser() {
    const cacheKey = "current_user";

    // Check cache first
    const cached = this.getCachedResponse(cacheKey);
    if (cached && !cached.cached) {
      return cached;
    }

    // Deduplicate concurrent requests
    return this.deduplicateRequest(cacheKey, async () => {
      try {
        const response = await axios.get(`${this.apiUrl}/auth/me`, {
          timeout: 10000,
        });

        const result = {
          success: true,
          user: response.data,
        };

        // Cache the result
        this.setCachedResponse(cacheKey, result);
        return result;
      } catch (error) {
        console.error("🔐 Get current user error:", error);

        // Return cached user if server is unreachable
        const cachedUser = this.getCurrentUserFromStorage();
        if (cachedUser) {
          return {
            success: true,
            user: cachedUser,
            cached: true,
          };
        }

        return {
          success: false,
          user: null,
          message:
            "Cannot connect to server and no cached user data available.",
        };
      }
    });
  }

  // Enhanced token refresh with request deduplication
  async refreshToken() {
    return this.deduplicateRequest("refresh_token", async () => {
      try {
        const response = await axios.post(
          `${this.apiUrl}/auth/refresh`,
          {},
          { timeout: 10000 }
        );

        if (response.data.access_token) {
          SecureStorage.setItem(this.tokenKey, response.data.access_token);

          this.logSecurityEvent("TOKEN_REFRESHED", {
            user_id: this.getCurrentUserFromStorage()?.id,
          });

          return true;
        }
        return false;
      } catch (error) {
        console.error("🔐 Token refresh failed:", error);
        this.logSecurityEvent("TOKEN_REFRESH_FAILED", {
          error: error.message,
          status: error.response?.status,
        });
        return false;
      }
    });
  }

  // Enhanced credential validation
  async validateCredentials(username, password) {
    const identifier = this.hashIdentifier(username);

    if (!this.rateLimiter.isAllowed(identifier)) {
      return {
        valid: false,
        message:
          "Too many validation attempts. Please wait before trying again.",
        rateLimited: true,
      };
    }

    try {
      this.logSecurityEvent("CREDENTIAL_VALIDATION_START", {
        username: this.maskUsername(username),
      });

      const response = await axios.post(
        `${this.apiUrl}/auth/validate-credentials`,
        { username, password },
        { timeout: 10000 }
      );

      this.logSecurityEvent("CREDENTIAL_VALIDATION_RESULT", {
        username: this.maskUsername(username),
        valid: response.data.valid,
      });

      if (!response.data.valid) {
        this.rateLimiter.recordAttempt(identifier);
      }

      return {
        valid: response.data.valid,
        message: response.data.message,
        user: response.data.user,
      };
    } catch (error) {
      this.rateLimiter.recordAttempt(identifier);

      console.error("🔐 Validate credentials error:", error);
      this.logSecurityEvent("CREDENTIAL_VALIDATION_ERROR", {
        username: this.maskUsername(username),
        error: error.message,
        status: error.response?.status,
      });

      return {
        valid: false,
        message:
          error.response?.data?.detail ||
          "Validation failed - server unavailable",
        user: null,
      };
    }
  }

  // Enhanced logout with cleanup
  logout() {
    try {
      this.logSecurityEvent("LOGOUT", {
        user_id: this.getCurrentUserFromStorage()?.id,
      });

      // Fire and forget - don't wait for server response
      axios.post(`${this.apiUrl}/auth/logout`).catch(() => {
        // Ignore errors on logout endpoint
      });
    } catch (error) {
      // Ignore logout errors
    } finally {
      // Clear all stored data
      this.clearAllData();
    }
  }

  // Clear all authentication data
  clearAllData() {
    SecureStorage.removeItem(this.tokenKey);
    SecureStorage.removeItem(this.userKey);
    SecureStorage.removeItem(this.securityLevelKey);

    this.stopActivityMonitoring();
    this.removeActivityListeners();

    // Clear cache
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.pendingRequests.clear();

    // Clear session storage
    try {
      sessionStorage.removeItem("security_events");
      sessionStorage.removeItem("login_flow_state");
      sessionStorage.removeItem("pending_credentials");
    } catch (error) {
      console.warn("Failed to clear session storage:", error);
    }

    // Clear axios default headers
    delete axios.defaults.headers.common["Authorization"];

    // Stop session monitoring
    this.stopSessionMonitoring();
  }

  // Authentication state checks (no API calls)
  isAuthenticated() {
    const token = SecureStorage.getItem(this.tokenKey);
    const user = SecureStorage.getItem(this.userKey);

    if (!token || !user) return false;

    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.warn("Invalid token format:", error);
      return false;
    }
  }

  getToken() {
    return SecureStorage.getItem(this.tokenKey);
  }

  getCurrentUserFromStorage() {
    const userStr = SecureStorage.getItem(this.userKey);
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.warn("Failed to parse stored user data:", error);
      return null;
    }
  }

  getSecurityLevel() {
    return SecureStorage.getItem(this.securityLevelKey) || "basic";
  }

  // Enhanced device fingerprinting
  getDeviceFingerprint() {
    let fingerprint = SecureStorage.getItem(this.deviceFingerprintKey);
    if (!fingerprint) {
      fingerprint = this.generateDeviceFingerprint();
      SecureStorage.setItem(this.deviceFingerprintKey, fingerprint);
    }
    return fingerprint;
  }

  generateDeviceFingerprint() {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillText("MedFlowHub Device ID", 2, 2);

      // Enhanced fingerprinting with more data points
      const fingerprint = {
        screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        languages: navigator.languages?.join(",") || "",
        platform: navigator.platform,
        userAgent: this.hashString(navigator.userAgent), // Hash instead of raw UA
        canvas: canvas.toDataURL(),
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: navigator.deviceMemory || 0,
        pixelRatio: window.devicePixelRatio || 1,
        touchSupport: "ontouchstart" in window,
        timestamp: Date.now(),
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        plugins: Array.from(navigator.plugins || [])
          .map((p) => p.name)
          .sort()
          .join(","),
        mimeTypes: Array.from(navigator.mimeTypes || [])
          .map((m) => m.type)
          .sort()
          .join(","),
      };

      const fingerprintString = JSON.stringify(fingerprint);
      return btoa(fingerprintString).substring(0, 64);
    } catch (error) {
      console.warn("Could not generate device fingerprint:", error);
      return btoa(
        JSON.stringify({
          timestamp: Date.now(),
          fallback: true,
          random: Math.random().toString(36),
          userAgent: this.hashString(navigator.userAgent),
        })
      );
    }
  }

  // Retry logic for authentication requests
  shouldRetryAuthRequest(error, config) {
    if (config.retryCount >= this.maxRetries) return false;

    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    const isRetryable =
      retryableStatuses.includes(error.response?.status) || !error.response;

    return isRetryable && !config.url.includes("/auth/login");
  }

  async retryAuthRequest(config) {
    const retryCount = config.retryCount || 0;
    const delay = this.retryDelay * Math.pow(2, retryCount); // Exponential backoff

    config.retryCount = retryCount + 1;

    console.log(
      `🔄 Retrying auth request (${retryCount + 1}/${
        this.maxRetries
      }) after ${delay}ms`
    );

    await this.delay(delay);
    return axios(config);
  }

  async handle401Error(config) {
    if (config._retry) {
      // Already tried to refresh, clear auth and redirect
      this.clearAllData();
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(new Error("Authentication failed"));
    }

    config._retry = true;
    console.log("🔐 401 Unauthorized - attempting token refresh");

    try {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        const newToken = SecureStorage.getItem(this.tokenKey);
        config.headers["Authorization"] = `Bearer ${newToken}`;
        return axios(config);
      }
    } catch (refreshError) {
      console.error("🔐 Token refresh failed:", refreshError);
    }

    // If refresh fails, clear auth data and redirect
    this.clearAllData();
    if (!window.location.pathname.includes("/login")) {
      window.location.href = "/login";
    }
    return Promise.reject(new Error("Token refresh failed"));
  }

  // Enhanced session monitoring
  startSessionMonitoring() {
    const token = this.getToken();
    if (!token) return;

    try {
      // Initialize activity tracking
      this.lastActivityTime = Date.now();
      this.isUserActive = true;

      // Setup activity listeners
      this.setupActivityListeners();

      // Start activity-based monitoring
      this.startActivityMonitoring();

      console.log("✅ Session monitoring started with activity tracking");
    } catch (error) {
      console.error("Error starting session monitoring:", error);
    }
  }

  setupActivityListeners() {
    // Remove existing listeners first
    this.removeActivityListeners();

    // Activity events that indicate user is active
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    // Throttled activity handler (max once per 5 seconds)
    let lastActivityUpdate = 0;
    this.activityHandler = () => {
      const now = Date.now();
      if (now - lastActivityUpdate > 5000) {
        // Throttle to once per 5 seconds
        this.lastActivityTime = now;
        this.isUserActive = true;
        lastActivityUpdate = now;

        // Check if token needs refresh
        this.checkAndRefreshToken();
      }
    };

    // Add listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, this.activityHandler, { passive: true });
    });

    // Store for cleanup
    this.activityEvents = activityEvents;
  }

  removeActivityListeners() {
    if (this.activityEvents && this.activityHandler) {
      this.activityEvents.forEach((event) => {
        window.removeEventListener(event, this.activityHandler);
      });
    }
  }

  startActivityMonitoring() {
    // Clear any existing monitoring
    this.stopActivityMonitoring();

    // Check activity every 60 seconds
    this.activityCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivityTime;

      // If user has been idle for longer than IDLE_TIMEOUT
      if (timeSinceLastActivity > this.IDLE_TIMEOUT) {
        console.log("🚪 User idle timeout - logging out");
        this.handleIdleTimeout();
        return;
      }

      // If user is active and token needs refresh
      if (this.isUserActive) {
        this.checkAndRefreshToken();
      }

      // Warn user 5 minutes before idle timeout
      const timeUntilTimeout = this.IDLE_TIMEOUT - timeSinceLastActivity;
      if (
        timeUntilTimeout <= 5 * 60 * 1000 &&
        timeUntilTimeout > 4 * 60 * 1000
      ) {
        this.warnIdleTimeout(Math.ceil(timeUntilTimeout / 60000));
      }
    }, 60000); // Check every minute
  }

  stopActivityMonitoring() {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
    if (this.sessionWarningTimeout) {
      clearTimeout(this.sessionWarningTimeout);
      this.sessionWarningTimeout = null;
    }
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }
    this.removeActivityListeners();
  }

  async checkAndRefreshToken() {
    const token = this.getToken();
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      // Refresh token if it expires in less than 10 minutes
      if (
        timeUntilExpiration < this.TOKEN_REFRESH_THRESHOLD &&
        timeUntilExpiration > 0
      ) {
        console.log("🔄 Auto-refreshing token for active user");
        const refreshed = await this.refreshToken();

        if (refreshed) {
          this.logSecurityEvent("TOKEN_AUTO_REFRESHED", {
            time_until_expiration: timeUntilExpiration,
            user_active: this.isUserActive,
          });

          // Restart monitoring with new token
          this.startSessionMonitoring();
        } else {
          console.warn("⚠️ Token refresh failed during activity");
        }
      }
    } catch (error) {
      console.error("Error checking token expiration:", error);
    }
  }

  handleIdleTimeout() {
    this.logSecurityEvent("IDLE_TIMEOUT", {
      idle_duration: Date.now() - this.lastActivityTime,
      idle_timeout: this.IDLE_TIMEOUT,
    });

    window.dispatchEvent(
      new CustomEvent("sessionExpired", {
        detail: { reason: "idle_timeout" },
      })
    );

    this.logout();
  }

  warnIdleTimeout(minutesRemaining) {
    this.logSecurityEvent("IDLE_WARNING", {
      minutes_remaining: minutesRemaining,
    });

    window.dispatchEvent(
      new CustomEvent("idleWarning", {
        detail: { minutesRemaining },
      })
    );
  }

  stopSessionMonitoring() {
    this.stopActivityMonitoring();
    this.removeActivityListeners();
  }

  // Enhanced error handling
  handleAuthError(error, defaultMessage) {
    if (!error.response) {
      return {
        success: false,
        message:
          "Cannot connect to server. Please check your network connection.",
        isNetworkError: true,
      };
    }

    const status = error.response.status;
    const detail = error.response.data?.detail || error.response.data?.message;

    switch (status) {
      case 400:
        return {
          success: false,
          message: detail || "Invalid request. Please check your input.",
        };
      case 401:
        return {
          success: false,
          message: detail || "Invalid credentials.",
        };
      case 403:
        return {
          success: false,
          message: detail || "Access denied. VPN connection may be required.",
        };
      case 423:
        return {
          success: false,
          message: detail || "Account is locked. Please try again later.",
        };
      case 429:
        const retryAfter = error.response.headers["retry-after"];
        return {
          success: false,
          message:
            detail ||
            `Too many attempts. Please wait ${
              retryAfter || 60
            } seconds before trying again.`,
          retryAfter: parseInt(retryAfter) || 60,
        };
      case 500:
        return {
          success: false,
          message: "Server error. Please try again later.",
        };
      default:
        return {
          success: false,
          message: detail || defaultMessage,
        };
    }
  }

  // Helper methods
  setAuthData(data) {
    try {
      SecureStorage.setItem(this.tokenKey, data.access_token);
      SecureStorage.setItem(this.userKey, JSON.stringify(data.user));
      SecureStorage.setItem(
        this.securityLevelKey,
        data.security_level || "basic"
      );

      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${data.access_token}`;

      // Set role-based idle timeout — medical professionals get longer sessions
      if (data.user?.role === "medical_professional") {
        this.IDLE_TIMEOUT = this.MP_IDLE_TIMEOUT;
      } else {
        this.IDLE_TIMEOUT = this.DEFAULT_IDLE_TIMEOUT;
      }

      this.logSecurityEvent("AUTH_DATA_STORED", {
        user_id: data.user.id,
        security_level: data.security_level,
        token_set: !!data.access_token,
      });

      // Start session monitoring
      this.startSessionMonitoring();

      return true;
    } catch (error) {
      console.error("Error storing auth data:", error);
      this.logSecurityEvent("AUTH_DATA_STORAGE_ERROR", {
        error: error.message,
      });
      return false;
    }
  }

  clearLoginState() {
    try {
      sessionStorage.removeItem("login_flow_state");
      sessionStorage.removeItem("pending_credentials");
      sessionStorage.removeItem("login_attempt_id");

      this.logSecurityEvent("LOGIN_STATE_CLEARED", {
        timestamp: Date.now(),
      });

      return true;
    } catch (error) {
      console.error("Error clearing login state:", error);
      return false;
    }
  }

  // Utility methods
  maskUsername(username) {
    if (!username || username.length < 3) return "***";
    return username.substring(0, 2) + "*".repeat(username.length - 2);
  }

  maskPhoneNumber(phone) {
    if (!phone || phone.length < 4) return "****";
    return (
      phone.substring(0, 2) +
      "*".repeat(phone.length - 4) +
      phone.substring(phone.length - 2)
    );
  }

  hashIdentifier(identifier) {
    return this.hashString(identifier);
  }

  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  generateRequestId() {
    return (
      "req_" + Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
    );
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  logSecurityEvent(eventType, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event_type: eventType,
      user_agent: navigator.userAgent,
      url: window.location.href,
      device_fingerprint: this.getDeviceFingerprint().substring(0, 10) + "...",
      details: details,
    };

    if (process.env.NODE_ENV === "development") {
      console.log("🔐 Security Event:", logEntry);
    }

    // Store in session storage with size limit
    try {
      const existingLogs = JSON.parse(
        sessionStorage.getItem("security_events") || "[]"
      );
      existingLogs.push(logEntry);

      // Keep only last 50 events to prevent storage overflow
      if (existingLogs.length > 50) {
        existingLogs.splice(0, existingLogs.length - 50);
      }

      sessionStorage.setItem("security_events", JSON.stringify(existingLogs));
    } catch (error) {
      console.warn("Could not store security event:", error);
    }
  }

  // Clear all caches
  clearCache() {
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.pendingRequests.clear();
  }

  // Get comprehensive stats
  getStats() {
    return {
      cache: {
        size: this.cache.size,
        memoryUsage: `${(this.getMemoryUsage() / (1024 * 1024)).toFixed(2)}MB`,
        maxSize: this.MAX_CACHE_SIZE,
        maxMemory: `${this.MAX_MEMORY_MB}MB`,
      },
      requests: {
        pending: this.pendingRequests.size,
      },
      rateLimiter: this.rateLimiter.getStats(),
      session: {
        isAuthenticated: this.isAuthenticated(),
        securityLevel: this.getSecurityLevel(),
        hasActiveMonitoring: !!(
          this.sessionCheckInterval || this.sessionWarningTimeout
        ),
      },
      config: {
        maxRetries: this.maxRetries,
        retryDelay: this.retryDelay,
      },
    };
  }
}

// Create and export singleton instance
const authService = new AuthService();

export default authService;
